import styled, { keyframes, css } from "styled-components"

const ring = keyframes`
  0% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-12deg); }
  30% { transform: rotate(10deg); }
  40% { transform: rotate(-8deg); }
  50% { transform: rotate(6deg); }
  60% { transform: rotate(-4deg); }
  70% { transform: rotate(2deg); }
  80% { transform: rotate(-1deg); }
  100% { transform: rotate(0deg); }
`

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`

const settle = keyframes`
  0% { transform: scale(1); opacity: 1; }
  40% { transform: scale(0.82); opacity: 0.55; }
  100% { transform: scale(1); opacity: 1; }
`

export const BellWrapper = styled.div<{ $collapsed: boolean }>`
  position: relative;
  width: 100%;
  padding: 0 0.6rem;
`

export const BellButton = styled.button<{ $collapsed: boolean }>`
  background: none;
  border: 1px solid transparent;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: ${(p) => (p.$collapsed ? "6rem" : "100%")};
  padding: 0.5rem 1rem;
  margin: 0;
  transition: all 0.2s;
  border-radius: 1rem;
  position: relative;
  color: #b0b0b0;
  text-decoration: none;
  font-size: 1.4rem;
  font-weight: 700;

  svg {
    width: 2rem;
    height: 2rem;
    fill: #000000;
    transition: all 0.2s;
  }

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);

    svg {
      fill: #ffffff;
    }
  }
`

export const BellIconWrapper = styled.span<{ $animate?: boolean }>`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.2rem;
  background: #4fd1c7;
  flex-shrink: 0;
  position: relative;
  transform-origin: top center;
  ${(p) =>
    p.$animate &&
    css`
      animation: ${ring} 0.8s ease-in-out;
    `}
`

export const BadgeDot = styled.span<{ $animate?: boolean; $settle?: boolean }>`
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  min-width: 1.8rem;
  height: 1.8rem;
  background: ${(p) => p.theme.colors.error.background};
  color: ${(p) => p.theme.colors.white};
  font-size: 1rem;
  font-weight: 700;
  border-radius: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.4rem;
  ${(p) =>
    p.$animate &&
    css`
      animation: ${pulse} 0.4s ease-in-out;
    `}
  ${(p) =>
    p.$settle &&
    css`
      animation: ${settle} 0.45s ease-out;
    `}
  border: 2px solid #0c2937;
  line-height: 1;
`

export const BellText = styled.div<{ $collapsed: boolean }>`
  display: ${(p) => (p.$collapsed ? "none" : "flex")};
  flex-direction: column;
  align-items: flex-start;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
  transition: opacity 0.2s;
`

export const BellMainText = styled.span`
  font-size: 1.4rem;
  font-weight: 900;
  color: ${(p) => p.theme.colors.white};
  line-height: 1.2;
`

export const BellSubText = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  color: ${(p) => p.theme.colors.white};
  line-height: 1.2;
  margin-top: 0.2rem;
  white-space: nowrap;
  opacity: 0.8;
`

export const DropdownWrapper = styled.div<{ $collapsed: boolean }>`
  position: fixed;
  bottom: 6rem;
  left: ${(p) => (p.$collapsed ? "7.2rem" : "26rem")};
  margin-left: 0.8rem;
  width: 30rem;
  background: #0c2937;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 10004;
  overflow: hidden;
  transition: left 0.3s ease;
`

export const DropdownHeader = styled.div`
  padding: 1.2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const DropdownSubtitle = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  opacity: 0.5;
  margin-left: 0.8rem;
`

export const DropdownItem = styled.button<{ $hasCount: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.2rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: ${(p) => (p.$hasCount ? p.theme.colors.white : p.theme.colors.greyDarker)};
  cursor: ${(p) => (p.$hasCount ? "pointer" : "default")};
  text-align: left;
  transition: background 0.2s;
  font-size: 1.3rem;

  &:hover {
    background: ${(p) => (p.$hasCount ? "rgba(255, 255, 255, 0.05)" : "none")};
  }

  &:last-child {
    border-bottom: none;
  }
`

export const ItemIcon = styled.span<{ $color: string }>`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$color};
  flex-shrink: 0;

  svg {
    width: 1.4rem;
    height: 1.4rem;
    fill: ${(p) => p.theme.colors.white};
  }
`

export const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

export const ItemTitle = styled.span`
  font-weight: 600;
  font-size: 1.3rem;
`

export const ItemCount = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  margin-left: auto;
  flex-shrink: 0;
`

export const EmptyState = styled.div`
  padding: 2rem 1.5rem;
  text-align: center;
  color: ${(p) => p.theme.colors.greyDarker};
  font-size: 1.3rem;
`
